import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams, useHistory } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { logisticEditSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import schema from "../../../../schemas/addLogisticSchema";
import Loader from "../../../../components/Loader";
import { reverse } from "named-urls";
import AddLogisticsForm from "../../../../forms/AddLogisticsForm";

const EditLogisticPartner = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: logisticEditSaga });
  const dispatch = useDispatch();
  const { logisticId } = useParams();
  const history = useHistory();

  // effects
  useEffect(() => {
    dispatch(actions.fetchLogisticDetails({ id: logisticId }));
    dispatch(actions.fetchCountries());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, logisticId]);

  // selectors
  const logisticData = useSelector(selectors.selectLogisticDetails);
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      pocName: "",
      pocContact: "",
      contract: "",
      operatingStates: [],
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
      },
      remarks: "",
    },
    resolver: yupResolver(schema),
  });

  const selectedCountry = watch("address.country");
  const selectedState = watch("address.state");

  useEffect(() => {
    if (selectedState) {
      // no operation for reset
    } else {
      reset(logisticData);
    }
  }, [reset, logisticData, selectedState]);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Updating Logistic",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "Logistic got added successfully",
    });
    history.push(reverse(routes.backOffice.logisticPartners.list));
  };

  const handleFailure = () => {
    console.log("An error occured");
  };

  const onSubmit = (data) => {
    const payload = {
      onSuccess: handleSuccess,
      data,
      onFailure: handleFailure,
    };
    dispatch(actions.updateLogistic(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Edit Logistic Partner Details"
        subTitle={logisticData?.name}
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.logisticPartners.list}>Cancel</Link>
          </Button>,
          <Button
            disabled={loading}
            loading={loading}
            key="2"
            type="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Update
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? (
          <Loader />
        ) : (
          <AddLogisticsForm
            options={{
              states,
              cities,
              countries,
            }}
            control={control}
            watch={watch}
            errors={errors}
            edit={true}
            setValue={setValue}
          />
        )}
      </div>
    </div>
  );
};

export default EditLogisticPartner;
